import { toast } from "sonner";
import { API } from "@/lib/api";
import { formatApiError } from "@/lib/apiError";

/**
 * Download the CAD starter kit for a render — a layered .3dm + per-BOM STLs.
 *
 * Pre-flight: the CAD layers ARE the BOM. Without saved materials the backend
 * 422s, so we surface the Materials drawer in that case instead of showing
 * a cryptic error.
 *
 * Uses `fetch` (not axios) because axios's default error path tries to read
 * responseText on a blob response, which throws synchronously in some browsers.
 *
 * @param {object} render          - The generation to export (must include `id` + optional `materials`).
 * @param {function} openMaterials - Callback invoked when no BOM is saved (or 422).
 * @param {string|null} tripoJobId - Optional: bundle a Tripo3D .glb mesh into the same archive.
 * @returns {Promise<boolean>}     - true on success, false otherwise.
 */
export async function downloadCadKit(render, openMaterials, tripoJobId = null) {
  if (!render) return false;

  if (!render.materials) {
    toast.message("Save a Bill of Materials first — the CAD layers come from your BOM.");
    openMaterials?.(render);
    return false;
  }

  try {
    const url = tripoJobId
      ? `${API}/studio/cad/${render.id}?tripo_job_id=${encodeURIComponent(tripoJobId)}`
      : `${API}/studio/cad/${render.id}`;
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/zip" },
    });

    if (!res.ok) {
      let msg =
        res.status === 422
          ? "Save a Bill of Materials first — the CAD layers come from your BOM."
          : `CAD export failed (${res.status}).`;
      try {
        const buf = await res.arrayBuffer();
        const text = new TextDecoder().decode(buf);
        try {
          const data = JSON.parse(text);
          // formatApiError handles `data.detail` whether it's a string or
          // a Pydantic 422 array of objects → no more "[object Object]".
          const parsed = formatApiError(data, "");
          if (parsed) msg = parsed;
        } catch (parseErr) {
          console.warn("CAD export error response was not JSON", parseErr);
          if (text) msg = text.slice(0, 300);
        }
      } catch (readErr) {
        console.warn("Could not read CAD error body", readErr);
      }
      if (res.status === 422) openMaterials?.(render);
      toast.error(msg);
      return false;
    }

    const blob = await res.blob();
    const size = blob?.size ?? 0;
    if (!blob || size < 100) {
      toast.error("CAD package was empty. Please try again.");
      return false;
    }

    const filename = `facetra_${render.id}_cad.zip`;

    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, filename);
      toast.success(`Downloaded · ${(size / 1024).toFixed(1)} KB`);
      return true;
    }

    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      try { document.body.removeChild(a); } catch { /* noop */ }
      try { window.URL.revokeObjectURL(blobUrl); } catch { /* noop */ }
    }, 1500);

    toast.success(`CAD kit downloaded · ${(size / 1024).toFixed(1)} KB`);
    return true;
  } catch (err) {
    toast.error(formatApiError(err, "CAD export failed."));
    return false;
  }
}
